import { useTranslation } from 'react-i18next';
import { Lock, LockOpen, Pencil, Trash2 } from 'lucide-react';

import type { TableActionsProps } from './types';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';

import { stripHtml } from '@/helpers';

export const TableActions = (props: TableActionsProps) => {
  const { edit, lock, remove, children } = props;
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      {lock && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              onClick={lock.onClick}
              className={cn(
                'size-8 p-0',
                lock.isLocked &&
                  'bg-destructive hover:bg-destructive/80 text-white hover:text-white',
              )}
            >
              {lock.isLocked ? (
                <Lock className="size-4" />
              ) : (
                <LockOpen className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {lock.isLocked ? t('Unlock') : t('Lock')}
          </TooltipContent>
        </Tooltip>
      )}
      {edit && 'url' in edit && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="size-8 p-0"
              asChild
            >
              <a href={edit.url}>
                <Pencil className="size-4" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('Edit')}</TooltipContent>
        </Tooltip>
      )}
      {edit && 'onClick' in edit && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="size-8 p-0"
              onClick={edit.onClick}
            >
              <Pencil className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('Edit')}</TooltipContent>
        </Tooltip>
      )}
      {remove && (
        <AlertDialog>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="size-8 p-0"
                >
                  <Trash2 className="size-4" />
                </Button>
              </AlertDialogTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('Delete')}</TooltipContent>
          </Tooltip>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('Are you sure you want to delete')}
                {remove.entityName && (
                  <span className="text-destructive font-bold">
                    {' '}
                    {stripHtml(remove.entityName)}
                  </span>
                )}
                {remove.id && (
                  <span className="text-destructive font-bold">
                    {' '}
                    {t('with ID')} {remove.id}
                  </span>
                )}
                ?
              </AlertDialogTitle>
              <AlertDialogDescription />
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('No')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive"
                onClick={remove.onConfirm}
              >
                {t('Yes')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {children}
    </div>
  );
};
